import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Patch, v1Post } from '@/lib/api';
import { fmtDateTimeIST } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { RecommendationPill } from '@/components/StatusPill';
import { VideoWidget } from '@/components/VideoWidget';
import { IntegrityReviewPanel } from '@/components/IntegrityReviewPanel';
import { VideoBotHumanRatingRow } from '@/components/HumanRatingsForms';
import { useHasPermission } from '@/auth/permissions';
import { RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  GENERIC_EVAL_FAILURE_MSG,
  isEvalUiStale,
  patchCandidateEvalPending,
} from '@/lib/evalErrors';
import { toastMutationError } from '@/lib/resumeEnrichmentErrors';

export function EmptyStateCard({ title, body }) {
  return (
    <Card className="card-elevated"><CardContent className="flex flex-col items-start gap-2 p-10 text-center">
      <p className="w-full text-base font-semibold text-foreground">{title}</p>
      <p className="w-full text-sm text-muted-foreground">{body}</p>
    </CardContent></Card>
  );
}

export function VideoBotIncludeSwitch({ candidateId, response, onChanged, canEdit = true }) {
  const qc = useQueryClient();
  const [included, setIncluded] = useState(!!response.client_curated);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setIncluded(!!response.client_curated);
  }, [response.id, response.client_curated]);

  const onToggle = async (next) => {
    if (!canEdit) return;
    setIncluded(next);
    setSaving(true);
    try {
      await v1Patch(`/candidates/${candidateId}/videobot/responses/${response.id}`, {
        client_curated: next,
      });
      toast.success(next ? 'Included in client report' : 'Excluded from client report');
      qc.invalidateQueries({ queryKey: ['v1-client-report-preview', candidateId] });
      onChanged?.();
    } catch (e) {
      setIncluded(!next);
      toastMutationError(e, 'Failed to update include flag');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`videobot-include-${response.id}`}>
      <Switch
        checked={included}
        disabled={saving || !canEdit}
        onCheckedChange={onToggle}
        aria-label="Include in client report"
      />
      <span className="text-xs text-muted-foreground">Include in client report</span>
    </div>
  );
}

export function VideoBotResponseCard({ r, index, candidateId, onChanged, isPlaying, onPlay, onStop }) {
  const qc = useQueryClient();
  const canEditEvaluation = useHasPermission('evaluation.edit');
  const layer1 = r.layer1 || {};
  const conceptsDetected = r.layer2_concepts_detected || [];
  const conceptsMissing = r.layer2_concepts_missing || [];
  const evalStatus = r.eval_status || 'pending';
  const [optimisticEvaluating, setOptimisticEvaluating] = useState(false);
  // Hand back to server truth as soon as hydrate reports any real status.
  React.useEffect(() => {
    if (optimisticEvaluating && evalStatus) setOptimisticEvaluating(false);
  }, [evalStatus, optimisticEvaluating]);
  // Stale applies only to in_progress (dead worker). Pending after reeval must show progress.
  const stale = evalStatus === 'in_progress' && isEvalUiStale(r.eval_started_at);
  const evalFailed = !optimisticEvaluating && (evalStatus === 'failed' || stale);
  const isEvaluating = optimisticEvaluating
    || (!evalFailed && (evalStatus === 'in_progress' || evalStatus === 'pending'));
  const hasAiEval = !optimisticEvaluating && evalStatus === 'completed';
  const verdictLabel = isEvaluating
    ? 'Evaluating…'
    : evalFailed
      ? 'Eval failed'
      : r.verdict === 'Strong'
        ? 'Strong Recommend'
        : r.verdict === 'Good'
          ? 'Recommend'
          : r.verdict === 'Weak'
            ? 'Weak'
            : 'Neutral';
  const durationDisplay = `00:${String(r.duration_seconds || 0).padStart(2, '0')}`;

  const reEval = async () => {
    setOptimisticEvaluating(true);
    patchCandidateEvalPending(qc, candidateId, { stage: 'videobot', entityId: r.id });
    try {
      await v1Post(`/candidates/${candidateId}/videobot/responses/${r.id}/reeval`, {});
      toast.success('Eval queued. Refreshing in a moment.');
      onChanged?.();
    } catch (e) {
      setOptimisticEvaluating(false);
      onChanged?.();
      toast.error(GENERIC_EVAL_FAILURE_MSG);
    }
  };

  return (
    <Card className="card-elevated" data-testid={`videobot-response-${index}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-semibold">Q{index}. {r.question_text}</p>
          <RecommendationPill label={verdictLabel} />
        </div>
        <VideoWidget
          mediaKey={r.id || r.question_id}
          url={r.video_url}
          duration={durationDisplay}
          isOpen={isPlaying}
          onPlayStateChange={(active) => {
            if (active) onPlay?.();
            else onStop?.();
          }}
        />
        {hasAiEval ? (
          <>
            {Object.keys(layer1).length > 0 && (
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {Object.entries(layer1).map(([k, v]) => (
                  <div key={k} className="rounded-lg border bg-card p-3">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</p>
                    <p className="text-lg font-semibold tabular-nums">{v}/5</p>
                  </div>
                ))}
              </div>
            )}
            {(conceptsDetected.length > 0 || conceptsMissing.length > 0 || r.red_flag_detected) && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground">Key concepts</p>
                <div className="flex flex-wrap gap-1.5">
                  {conceptsDetected.map((c) => <span key={c} className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200">{c}</span>)}
                  {conceptsMissing.map((c) => <span key={c} className="rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground border">{c}</span>)}
                  {r.red_flag_detected && (
                    <span className="rounded-md bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 border border-rose-200" title={r.red_flag_reason || ''}>
                      Flag Detected
                    </span>
                  )}
                </div>
              </div>
            )}
            {r.red_flag_detected && r.red_flag_reason && (
              <p className="text-xs text-rose-700"><span className="font-semibold">Red flag:</span> {r.red_flag_reason}</p>
            )}
            {r.eval_summary && (
              <p className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">AI summary:</span> {r.eval_summary}</p>
            )}
            {r.transcript && (
              <details className="rounded-lg border bg-card p-3 text-xs">
                <summary className="cursor-pointer font-medium text-muted-foreground">View transcript</summary>
                <p className="mt-2 whitespace-pre-wrap text-foreground">{r.transcript}</p>
              </details>
            )}
            <div className="flex items-center justify-between rounded-lg border bg-secondary/40 p-3">
              <div>
                <p className="text-xs text-muted-foreground">Overall score (AI draft)</p>
                <p className="text-2xl font-semibold tabular-nums">{r.overall_score || 0}<span className="text-sm text-muted-foreground">/5</span></p>
              </div>
              <div className="flex items-center gap-3">
                {canEditEvaluation && (
                  <Button size="sm" variant="ghost" onClick={reEval} data-testid={`videobot-reeval-${index}`}>
                    <RefreshCw className="mr-1 h-3.5 w-3.5" /> Re-evaluate
                  </Button>
                )}
                <VideoBotIncludeSwitch
                  candidateId={candidateId}
                  response={r}
                  onChanged={onChanged}
                  canEdit={canEditEvaluation}
                />
              </div>
            </div>
            <VideoBotHumanRatingRow
              candidateId={candidateId}
              response={r}
              readOnly={!canEditEvaluation}
            />
            {r.eval_failure_reason && (
              <p className="text-xs text-muted-foreground" data-testid={`videobot-eval-failure-reason-${index}`}>
                {r.eval_failure_reason}
              </p>
            )}
          </>
        ) : evalFailed ? (
          <div className="space-y-1" data-testid={`videobot-eval-failed-${index}`}>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs text-rose-600">{GENERIC_EVAL_FAILURE_MSG}</p>
              {canEditEvaluation && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-700" onClick={reEval} data-testid={`videobot-retry-${index}`}>
                  <RefreshCw className="mr-1 h-3 w-3" /> Retry
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-dashed bg-secondary/30 p-3 text-xs text-muted-foreground" data-testid={`videobot-eval-pending-${index}`}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>
              AI evaluation in progress — refresh in a few seconds.
              {r.status && <span className="ml-1 italic">(response: {r.status})</span>}
            </span>
          </div>
        )}
        {r.hr_note && <p className="text-xs text-muted-foreground"><span className="font-semibold">HR note:</span> {r.hr_note}</p>}
      </CardContent>
    </Card>
  );
}

export function McqResultView({
  result, evalBlock, recording, integrityReview, submittedAt, assessmentId, candidateId, onChanged,
  reevalPath, testIdPrefix = 'mcq', stageCode = 'MCQ',
}) {
  const qc = useQueryClient();
  const canEditEvaluation = useHasPermission('evaluation.edit');
  const evalStatus = evalBlock?.eval_status || 'pending';
  const [optimisticEvaluating, setOptimisticEvaluating] = useState(false);
  const cacheStage = stageCode === 'TECH_ASSESSMENT' ? 'tech' : 'mcq';
  React.useEffect(() => {
    if (optimisticEvaluating && evalStatus) setOptimisticEvaluating(false);
  }, [evalStatus, optimisticEvaluating]);
  const stale = evalStatus === 'in_progress' && isEvalUiStale(evalBlock?.eval_started_at);
  const evalFailed = !optimisticEvaluating && (evalStatus === 'failed' || stale);
  const isEvaluating = optimisticEvaluating
    || (!evalFailed && (evalStatus === 'in_progress' || evalStatus === 'pending'));
  const evalDone = !optimisticEvaluating && evalStatus === 'completed';
  const verdictLabel = isEvaluating
    ? 'Evaluating…'
    : evalFailed
      ? 'Eval failed'
      : evalBlock?.verdict === 'Strong'
        ? 'Strong Recommend'
        : evalBlock?.verdict === 'Good'
          ? 'Recommend'
          : evalBlock?.verdict === 'Weak'
            ? 'Weak'
            : 'Neutral';

  const recStatus = recording?.status || 'none';
  const recDuration = recording?.duration_seconds
    ? `${Math.floor(recording.duration_seconds / 60)}:${String(recording.duration_seconds % 60).padStart(2, '0')}`
    : undefined;
  const recordingAvailable = recStatus === 'available' && !!recording?.video_url;
  const recordingUnavailable = recStatus === 'failed' || recStatus === 'unavailable'
    || recStatus === 'pending';
  const videoRef = React.useRef(null);
  const [currentTimeSec, setCurrentTimeSec] = React.useState(0);

  const reEval = async () => {
    setOptimisticEvaluating(true);
    patchCandidateEvalPending(qc, candidateId, { stage: cacheStage, entityId: assessmentId });
    try {
      await v1Post(reevalPath || `/candidates/${candidateId}/mcq/assessment/${assessmentId}/reeval`, {});
      toast.success('AI eval queued. Refreshing shortly.');
      onChanged?.();
    } catch (e) {
      setOptimisticEvaluating(false);
      onChanged?.();
      toast.error(GENERIC_EVAL_FAILURE_MSG);
    }
  };
  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-result-view`}>
      <Card className="card-elevated"><CardContent className="p-5">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <KpiTile label="Score" value={`${result.score_pct}%`} />
          <KpiTile label="Correct" value={`${result.correct} / ${result.total_questions}`} />
          <KpiTile label="Time taken" value={`${Math.floor(result.time_taken_seconds / 60)}m ${result.time_taken_seconds % 60}s`} />
          <KpiTile label="Percentile" value={result.pool_size >= 5 ? `Top ${100 - result.percentile}%` : 'Pending'} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Submitted on {submittedAt ? fmtDateTimeIST(submittedAt) : '—'}</p>
      </CardContent></Card>

      <Card className="card-elevated" data-testid={`${testIdPrefix}-session-recording`}>
        <CardContent className="p-5 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold">Session recording</p>
            {integrityReview?.disposition && (
              <span className="text-xs text-muted-foreground">
                Integrity: <span className="font-semibold capitalize">{integrityReview.disposition}</span>
              </span>
            )}
          </div>
          {recordingAvailable ? (
            <VideoWidget
              ref={videoRef}
              interactive
              url={recording.video_url}
              title="Session recording"
              duration={recDuration}
              onTimeUpdate={setCurrentTimeSec}
            />
          ) : recordingUnavailable ? (
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Recording unavailable</p>
              {recording?.error && (
                <p className="text-xs text-muted-foreground">Reason: {recording.error}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No session recording for this attempt</p>
          )}
        </CardContent>
      </Card>

      <IntegrityReviewPanel
        candidateId={candidateId}
        assessmentId={assessmentId}
        stageCode={stageCode}
        integrityReview={integrityReview}
        recordingAvailable={recordingAvailable}
        videoRef={videoRef}
        currentTimeSec={currentTimeSec}
        onChanged={onChanged}
        testIdPrefix={testIdPrefix}
        readOnly={!canEditEvaluation}
      />

      {/* Sprint 11.10 — AI eval card (verdict / summary / strengths / weaknesses) */}
      <Card className="card-elevated">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between">
            <p className="text-sm font-semibold">AI evaluation</p>
            <div className="flex items-center gap-2">
              <RecommendationPill label={verdictLabel} />
              {(evalDone || evalFailed) && canEditEvaluation && (
                <Button size="sm" variant="ghost" onClick={reEval} data-testid={`${testIdPrefix}-reeval-btn`}>
                  <RefreshCw className="mr-1 h-3.5 w-3.5" /> Re-evaluate
                </Button>
              )}
            </div>
          </div>
          {evalDone ? (
            <>
              {evalBlock.eval_summary && (
                <p className="text-sm" data-testid="mcq-eval-summary">{evalBlock.eval_summary}</p>
              )}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {evalBlock.strengths?.length > 0 && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <p className="text-xs font-semibold text-emerald-900">Strengths</p>
                    <ul className="mt-1 space-y-0.5 text-xs text-emerald-800">
                      {evalBlock.strengths.map((s) => <li key={s}>· {s}</li>)}
                    </ul>
                  </div>
                )}
                {evalBlock.weaknesses?.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-semibold text-amber-900">Weaknesses</p>
                    <ul className="mt-1 space-y-0.5 text-xs text-amber-800">
                      {evalBlock.weaknesses.map((s) => <li key={s}>· {s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              {(evalBlock.concepts_strong?.length > 0 || evalBlock.concepts_weak?.length > 0) && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">Concepts</p>
                  <div className="flex flex-wrap gap-1.5">
                    {evalBlock.concepts_strong?.map((c) => <span key={`s-${c}`} className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200">{c}</span>)}
                    {evalBlock.concepts_weak?.map((c) => <span key={`w-${c}`} className="rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground border">{c}</span>)}
                  </div>
                </div>
              )}
              {evalBlock.red_flag_detected && (
                <p className="text-xs text-rose-700"><span className="font-semibold">Red flag:</span> {evalBlock.red_flag_reason || 'detected'}</p>
              )}
              {evalBlock?.eval_failure_reason && (
                <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-eval-failure-reason`}>
                  {evalBlock.eval_failure_reason}
                </p>
              )}
            </>
          ) : evalFailed ? (
            <div className="space-y-1" data-testid="mcq-eval-failed">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs text-rose-600">{GENERIC_EVAL_FAILURE_MSG}</p>
                {canEditEvaluation && (
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-rose-700" onClick={reEval} data-testid={`${testIdPrefix}-retry-btn`}>
                    <RefreshCw className="mr-1 h-3 w-3" /> Retry
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-dashed bg-secondary/30 p-3 text-xs text-muted-foreground" data-testid="mcq-eval-pending">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>AI commentary is being generated — refresh in a few seconds.</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="card-elevated"><CardContent className="p-5">
          <p className="text-sm font-semibold">Difficulty breakdown</p>
          <ul className="mt-3 space-y-2">
            {result.difficulty_breakdown.map((d) => (
              <li key={d.level}>
                <div className="flex items-center justify-between text-xs"><span className="text-muted-foreground">{d.level}</span><span className="tabular-nums">{d.correct}/{d.total}</span></div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary" style={{ width: `${(d.correct / Math.max(1, d.total)) * 100}%` }} /></div>
              </li>
            ))}
          </ul>
        </CardContent></Card>
        <Card className="card-elevated"><CardContent className="p-5">
          <p className="text-sm font-semibold">Topic breakdown</p>
          <ul className="mt-3 grid grid-cols-2 gap-2 text-xs">
            {result.topic_breakdown.map((t) => (
              <li key={t.topic} className="flex items-center justify-between rounded-md border bg-secondary/40 px-2 py-1.5"><span className="truncate">{t.topic}</span><span className="tabular-nums font-medium">{t.correct}/{t.total}</span></li>
            ))}
          </ul>
        </CardContent></Card>
      </div>
      <Card className="card-elevated"><CardContent className="p-5">
        <p className="text-sm font-semibold">Per-question result</p>
        <p className="text-xs text-muted-foreground">Visible to HR only. Individual answers are not shown to clients.</p>
        <ul className="mt-3 space-y-2">
          {result.per_question.map((q, i) => (
            <li key={q.qid} className="flex items-start gap-3 rounded-lg border bg-card p-3">
              <span className={`mt-1 inline-block h-2 w-2 rounded-full ${q.is_correct ? 'bg-emerald-500' : 'bg-rose-500'}`} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{i + 1}. {q.text}</p>
                <p className="text-xs text-muted-foreground">Chosen: {q.chosen >= 0 ? String.fromCharCode(65 + q.chosen) : '—'} · Correct: {String.fromCharCode(65 + q.correct)}</p>
              </div>
            </li>
          ))}
        </ul>
      </CardContent></Card>
    </div>
  );
}

export function KpiTile({ label, value }) {
  return <div className="rounded-lg border bg-card p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p></div>;
}